import React, { useState, useEffect } from 'react';
import { Card, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import apiService from '@/services/api.service';
import { logger } from '@/utils/logger';
import { 
    BankOutlined,
    TeamOutlined,
    UserOutlined,
    TrophyOutlined,
    DollarOutlined
} from '@ant-design/icons';
import { API_ENDPOINTS } from '@/constants/api.constants';

const { Title } = Typography;

// Стили для карточек статистики
const StatCardStyle: React.CSSProperties = {
    borderRadius: '24px',
    boxShadow: 
        '0 8px 24px rgba(24, 144, 255, 0.3), 0 4px 12px rgba(24, 144, 255, 0.2)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: 'none',
    background: '#1890ff',
    color: 'white',
    cursor: 'pointer'
};

const StatCardStyleSecondary: React.CSSProperties = {
    ...StatCardStyle,
    background: '#ff4d4f',
    boxShadow: 
        '0 8px 24px rgba(255, 77, 79, 0.3), 0 4px 12px rgba(255, 77, 79, 0.2)'
};

const StatCardStyleSuccess: React.CSSProperties = {
    ...StatCardStyle,
    background: '#52c41a',
    boxShadow: 
        '0 8px 24px rgba(82, 196, 26, 0.3), 0 4px 12px rgba(82, 196, 26, 0.2)'
};

const StatCardStyleWarning: React.CSSProperties = {
    ...StatCardStyle,
    background: '#faad14',
    boxShadow: 
        '0 8px 24px rgba(250, 173, 20, 0.3), 0 4px 12px rgba(250, 173, 20, 0.2)'
};

const DashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        federations: { total: 0, active: 0 },
        clubs: { total: 0, active: 0 },
        managers: { total: 0, active: 0 },
        coaches: { total: 0, active: 0 },
        participants: { total: 0 },
        tournaments: { total: 0, published: 0 },
        subscriptions: { total: 0, active: 0, revenue: 0 }
    });

    useEffect(() => {
        const loadStats = async () => {
            try {
                const [federationsRes, clubsRes, managersRes, coachesRes, participantsRes, tournamentsRes, subscriptionsRes] = await Promise.all([
                    apiService.get<any>(API_ENDPOINTS.FEDERATIONS.BASE, { params: { limit: 1000 } }),
                    apiService.get<any>(API_ENDPOINTS.CLUBS.BASE, { params: { limit: 1000 } }),
                    apiService.get<any>(API_ENDPOINTS.MANAGERS.BASE),
                    apiService.get<any>(API_ENDPOINTS.COACHES.BASE),
                    apiService.get<any>(API_ENDPOINTS.PARTICIPANTS.BASE),
                    apiService.get<any>(API_ENDPOINTS.TOURNAMENTS.BASE),
                    apiService.get<any>(API_ENDPOINTS.SUBSCRIPTIONS.STATISTICS).catch(() => ({ data: { data: null } }))
                ]);

                const federationsData = federationsRes?.data?.data;
                let federations: any[] = [];
                if (Array.isArray(federationsData)) {
                    federations = federationsData;
                } else if (federationsData && typeof federationsData === 'object') {
                    // API федераций возвращает объект с полем items
                    if ('items' in federationsData && Array.isArray((federationsData as any).items)) {
                        federations = (federationsData as any).items;
                    } else if ('rows' in federationsData && Array.isArray((federationsData as any).rows)) {
                        federations = (federationsData as any).rows;
                    } else if ('data' in federationsData && Array.isArray((federationsData as any).data)) {
                        federations = (federationsData as any).data;
                    }
                }
                
                const clubsData = clubsRes?.data?.data;
                let clubs: any[] = [];
                if (Array.isArray(clubsData)) {
                    clubs = clubsData;
                } else if (clubsData && typeof clubsData === 'object') {
                    // API клубов возвращает объект с полем rows
                    if ('rows' in clubsData && Array.isArray((clubsData as any).rows)) {
                        clubs = (clubsData as any).rows;
                    } else if ('items' in clubsData && Array.isArray((clubsData as any).items)) {
                        clubs = (clubsData as any).items;
                    } else if ('data' in clubsData && Array.isArray((clubsData as any).data)) {
                        clubs = (clubsData as any).data;
                    }
                }
                
                const managersData = managersRes?.data?.data;
                let managers: any[] = [];
                if (Array.isArray(managersData)) {
                    managers = managersData;
                } else if (managersData && typeof managersData === 'object') {
                    if ('rows' in managersData && Array.isArray((managersData as any).rows)) {
                        managers = (managersData as any).rows;
                    } else if ('items' in managersData && Array.isArray((managersData as any).items)) {
                        managers = (managersData as any).items;
                    } else if ('data' in managersData && Array.isArray((managersData as any).data)) {
                        managers = (managersData as any).data;
                    }
                }
                
                const coachesData = coachesRes?.data?.data;
                let coaches: any[] = [];
                if (Array.isArray(coachesData)) {
                    coaches = coachesData;
                } else if (coachesData && typeof coachesData === 'object') {
                    if ('rows' in coachesData && Array.isArray((coachesData as any).rows)) {
                        coaches = (coachesData as any).rows;
                    } else if ('items' in coachesData && Array.isArray((coachesData as any).items)) {
                        coaches = (coachesData as any).items;
                    } else if ('data' in coachesData && Array.isArray((coachesData as any).data)) {
                        coaches = (coachesData as any).data;
                    }
                }
                
                const participantsData = participantsRes?.data?.data;
                let participants: any[] = [];
                if (Array.isArray(participantsData)) {
                    participants = participantsData;
                } else if (participantsData && typeof participantsData === 'object') {
                    if ('rows' in participantsData && Array.isArray((participantsData as any).rows)) {
                        participants = (participantsData as any).rows;
                    } else if ('items' in participantsData && Array.isArray((participantsData as any).items)) {
                        participants = (participantsData as any).items;
                    }
                }
                
                const tournamentsData = tournamentsRes?.data?.data;
                let tournaments: any[] = [];
                if (Array.isArray(tournamentsData)) {
                    tournaments = tournamentsData;
                } else if (tournamentsData && typeof tournamentsData === 'object') {
                    if ('rows' in tournamentsData && Array.isArray((tournamentsData as any).rows)) {
                        tournaments = (tournamentsData as any).rows;
                    } else if ('items' in tournamentsData && Array.isArray((tournamentsData as any).items)) {
                        tournaments = (tournamentsData as any).items;
                    }
                }

                setStats({
                    federations: {
                        total: federations.length,
                        active: federations.filter((f: any) => f.status === 'active').length
                    },
                    clubs: {
                        total: clubs.length,
                        active: clubs.filter((c: any) => c.status === 'active').length
                    },
                    managers: {
                        total: managers.length,
                        active: managers.filter((m: any) => m.status === 'active').length
                    },
                    coaches: {
                        total: coaches.length,
                        active: coaches.filter((c: any) => c.status === 'active').length
                    },
                    participants: {
                        total: participants.length
                    },
                    tournaments: {
                        total: tournaments.length,
                        published: tournaments.filter((t: any) => t.status === 'published').length
                    },
                    subscriptions: {
                        total: (subscriptionsRes?.data?.data && typeof subscriptionsRes.data.data === 'object' && 'total' in subscriptionsRes.data.data) ? (subscriptionsRes.data.data as any).total || 0 : 0,
                        active: (subscriptionsRes?.data?.data && typeof subscriptionsRes.data.data === 'object' && 'active' in subscriptionsRes.data.data) ? (subscriptionsRes.data.data as any).active || 0 : 0,
                        revenue: (subscriptionsRes?.data?.data && typeof subscriptionsRes.data.data === 'object' && 'totalRevenue' in subscriptionsRes.data.data) ? (subscriptionsRes.data.data as any).totalRevenue || 0 : 0
                    }
                });
            } catch (error) {
                logger.error('Error loading dashboard stats:', error);
            }
        };

        loadStats();
    }, []);

    return (
        <div>
            <Title level={2} style={{ marginBottom: 24 }}>Дашборд</Title>
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '16px',
                marginBottom: 24
            }}>
                <Card 
                    style={StatCardStyle}
                    styles={{ body: { padding: '20px' } }}
                    hoverable
                    onClick={() => navigate('/admin/federations')}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: 8 }}>
                                {stats.federations.total}
                            </div>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>Федераций</div>
                            <div style={{ fontSize: '12px', opacity: 0.7, marginTop: 4 }}>
                                Активных: {stats.federations.active}
                            </div>
                        </div>
                        <BankOutlined style={{ fontSize: '48px', opacity: 0.3 }} />
                    </div>
                </Card>
                <Card 
                    style={StatCardStyleSecondary}
                    styles={{ body: { padding: '20px' } }}
                    hoverable
                    onClick={() => navigate('/admin/clubs')}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: 8 }}>
                                {stats.clubs.total}
                            </div>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>Клубов</div>
                            <div style={{ fontSize: '12px', opacity: 0.7, marginTop: 4 }}>
                                Активных: {stats.clubs.active}
                            </div>
                        </div>
                        <TeamOutlined style={{ fontSize: '48px', opacity: 0.3 }} />
                    </div>
                </Card>
                <Card 
                    style={StatCardStyleSuccess}
                    styles={{ body: { padding: '20px' } }}
                    hoverable
                    onClick={() => navigate('/admin/managers')}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: 8 }}>
                                {stats.managers.total}
                            </div>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>Менеджеров</div>
                            <div style={{ fontSize: '12px', opacity: 0.7, marginTop: 4 }}>
                                Активных: {stats.managers.active}
                            </div>
                        </div>
                        <UserOutlined style={{ fontSize: '48px', opacity: 0.3 }} />
                    </div>
                </Card>
                <Card 
                    style={StatCardStyleWarning}
                    styles={{ body: { padding: '20px' } }}
                    hoverable
                    onClick={() => navigate('/admin/participants')}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: 8 }}>
                                {stats.participants.total}
                            </div>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>Спортсменов</div>
                        </div>
                        <TeamOutlined style={{ fontSize: '48px', opacity: 0.3 }} />
                    </div>
                </Card>
                <Card 
                    style={StatCardStyle}
                    styles={{ body: { padding: '20px' } }}
                    hoverable
                    onClick={() => navigate('/admin/tournaments')}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: 8 }}>
                                {stats.tournaments.total}
                            </div>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>Турниров</div>
                            <div style={{ fontSize: '12px', opacity: 0.7, marginTop: 4 }}>
                                Опубликовано: {stats.tournaments.published}
                            </div>
                        </div>
                        <TrophyOutlined style={{ fontSize: '48px', opacity: 0.3 }} />
                    </div>
                </Card>
                <Card 
                    style={StatCardStyleSecondary}
                    styles={{ body: { padding: '20px' } }}
                    hoverable
                    onClick={() => navigate('/admin/subscriptions')}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: 8 }}>
                                {stats.subscriptions.total}
                            </div>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>Подписок</div>
                            <div style={{ fontSize: '12px', opacity: 0.7, marginTop: 4 }}>
                                Активных: {stats.subscriptions.active}
                            </div>
                        </div>
                        <DollarOutlined style={{ fontSize: '48px', opacity: 0.3 }} />
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default DashboardPage;


